import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import { ChecklistItemRenderer } from './ChecklistItemRenderer';
import { MechanicChecklistRow } from './MechanicChecklistRow';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  useBookingChecklist,
  useUpdateChecklistItem,
  useCompleteChecklist,
  useChecklistPhotoUploadUrl,
  useRegisterChecklistPhoto,
  useDeleteChecklistPhoto,
  useGenerateChecklistReport,
  type BookingChecklistEntry,
  type ChecklistTemplateItem,
  type ChecklistPhoto,
} from '@/hooks/useChecklists';
import { useNotify } from '@/stores/notificationStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useState } from 'react';
import { describeApiError } from '@/lib/describeApiError';

interface Props {
  checklistId: string | null;
  onClose: () => void;
  /**
   * Render the mechanic-optimized compact rows (one-tap completion, inline note
   * and same-row camera actions) instead of the default form-like renderer.
   * Defaults to true so the mechanic flow gets the optimized experience.
   */
  mechanicMode?: boolean;
}

export function ChecklistFillDialog({ checklistId, onClose, mechanicMode = true }: Props) {
  const { t } = useTranslation('checklists');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { data: checklist } = useBookingChecklist(checklistId);
  const updateItem = useUpdateChecklistItem();
  const completeChecklist = useCompleteChecklist();
  const getUploadUrl = useChecklistPhotoUploadUrl();
  const registerPhoto = useRegisterChecklistPhoto();
  const deleteChecklistPhoto = useDeleteChecklistPhoto();
  const generateReport = useGenerateChecklistReport();
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState<string | null>(null);

  const debounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const items = useMemo(
    () => (checklist?.items ?? []) as ChecklistTemplateItem[],
    [checklist?.items],
  );

  const entriesMap = useMemo(() => {
    const map = new Map<string, BookingChecklistEntry>();
    for (const entry of (checklist?.completedItems ?? []) as BookingChecklistEntry[]) {
      map.set(entry.itemId, entry);
    }
    return map;
  }, [checklist?.completedItems]);

  const progress = checklist
    ? Math.round(((checklist.completedCount ?? 0) / Math.max(checklist.totalItems, 1)) * 100)
    : 0;

  const isCompleted = checklist?.status === 'completed';

  const requiredItemsFilled = useMemo(() => {
    return items
      .filter((item) => item.required)
      .every((item) => entriesMap.get(item.id)?.checked);
  }, [items, entriesMap]);

  const handleItemChange = useCallback(
    (entry: Partial<BookingChecklistEntry> & { itemId: string }) => {
      if (!checklistId) return;

      const existing = debounceTimers.current.get(entry.itemId);
      if (existing) clearTimeout(existing);

      const timer = setTimeout(() => {
        updateItem.mutate({
          checklistId,
          itemId: entry.itemId,
          checked: entry.checked ?? false,
          value: entry.value,
          notes: entry.notes,
        });
        debounceTimers.current.delete(entry.itemId);
      }, 500);

      debounceTimers.current.set(entry.itemId, timer);
    },
    [checklistId, updateItem],
  );

  // Group photos by itemId
  const photosByItem = useMemo(() => {
    const map = new Map<string, ChecklistPhoto[]>();
    for (const photo of (checklist?.photos ?? []) as ChecklistPhoto[]) {
      const existing = map.get(photo.itemId) ?? [];
      existing.push(photo);
      map.set(photo.itemId, existing);
    }
    return map;
  }, [checklist?.photos]);

  const handleAddPhoto = useCallback(
    async (itemId: string, file: File) => {
      if (!checklistId) return;
      setUploadingItemId(itemId);
      try {
        const ext = file.name.split('.').pop() ?? 'jpg';
        const { uploadUrl, fileKey, fileUrl } = await getUploadUrl.mutateAsync({
          checklistId,
          itemId,
          mimeType: file.type || 'image/jpeg',
          fileExtension: ext,
        });

        // Upload to S3
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'image/jpeg' },
        });

        // Register photo
        await registerPhoto.mutateAsync({
          checklistId,
          itemId,
          fileKey,
          fileUrl,
          mimeType: file.type || 'image/jpeg',
          fileSizeBytes: file.size,
        });
      } catch (error) {
        notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.photoUploadFailed') }));
      } finally {
        setUploadingItemId(null);
      }
    },
    [checklistId, getUploadUrl, registerPhoto, notify, tc],
  );

  const handleDeletePhoto = useCallback(
    async (_itemId: string, photoId: string) => {
      if (!checklistId) return;
      try {
        await deleteChecklistPhoto.mutateAsync({
          checklistId,
          photoId,
        });
      } catch (error) {
        notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.photoDeleteFailed') }));
      }
    },
    [checklistId, deleteChecklistPhoto, notify, tc],
  );

  const handleComplete = async () => {
    if (!checklistId) return;
    try {
      await completeChecklist.mutateAsync(checklistId);
      notify.success(t('booking.completed'));
      setConfirmComplete(false);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.completeFailed') }));
    }
  };

  const handleGenerateReport = async () => {
    if (!checklistId) return;
    try {
      const result = await generateReport.mutateAsync({ checklistId });
      window.open(result.downloadUrl, '_blank');
      notify.success(t('booking.reportGenerated'));
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.reportFailed') }));
    }
  };

  return (
    <>
      <ResponsiveDialog
        mobileTitle={checklist?.name ?? ''}
        open={!!checklistId}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogHeader
          title={checklist?.name ?? ''}
          extra={
            <Chip
              size="small"
              label={t(`status.${checklist?.status ?? 'pending'}`)}
              color={isCompleted ? 'success' : 'default'}
            />
          }
          onClose={onClose}
        />

        <DialogContent dividers>
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="body2" color="text.secondary">
                {t('booking.progress', {
                  completed: checklist?.completedCount ?? 0,
                  total: checklist?.totalItems ?? 0,
                })}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={isCompleted ? 'success' : 'primary'}
              sx={{ borderRadius: 1, height: 6 }}
            />
          </Box>

          <Stack spacing={1}>
            {items.map((item) => {
              const Renderer = mechanicMode ? MechanicChecklistRow : ChecklistItemRenderer;
              return (
                <Renderer
                  key={item.id}
                  item={item}
                  entry={entriesMap.get(item.id)}
                  onChange={handleItemChange}
                  disabled={isCompleted}
                  photos={photosByItem.get(item.id)}
                  onAddPhoto={handleAddPhoto}
                  onDeletePhoto={handleDeletePhoto}
                  isUploadingPhoto={uploadingItemId === item.id}
                />
              );
            })}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>{tc('close')}</Button>
          {isCompleted && (
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => void handleGenerateReport()}
              disabled={generateReport.isPending}
            >
              {t('booking.generateReport')}
            </Button>
          )}
          {!isCompleted && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => setConfirmComplete(true)}
              disabled={!requiredItemsFilled || completeChecklist.isPending}
            >
              {t('booking.markComplete')}
            </Button>
          )}
        </DialogActions>
      </ResponsiveDialog>

      <ConfirmDialog
        open={confirmComplete}
        onCancel={() => setConfirmComplete(false)}
        onConfirm={() => void handleComplete()}
        title={t('booking.markComplete')}
        message={t('booking.confirmComplete')}
      />
    </>
  );
}
